/**
 * browser-notification.service.ts
 *
 * Helpers para exibir notificações pop-up NATIVAS do navegador (Notification API),
 * disparadas localmente quando uma nova notificação chega pelo socket.
 *
 * Não confundir com o Web Push (push.service.ts), que depende do backend enviar
 * mensagens push via service worker. Aqui o dado já chegou ao app pelo socket.
 */
import { AppNotification } from "@/services/notification/notificacoes.service";

const NOTIFICATION_ICON = "/img/icons/android-chrome-192x192.png";

/** Indica se o navegador suporta a Notification API. */
export function isBrowserNotificationSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

/**
 * Solicita a permissão de notificação ao navegador.
 * Deve ser chamada a partir de um gesto do usuário (ex.: clique no toggle).
 * Retorna "denied" quando o navegador não suporta a API.
 */
export async function requestBrowserNotificationPermission(): Promise<NotificationPermission> {
  if (!isBrowserNotificationSupported()) return "denied";
  if (Notification.permission === "granted") return "granted";
  return await Notification.requestPermission();
}

/**
 * Exibe uma notificação pop-up nativa do navegador.
 * Respeita a permissão concedida pelo navegador: só exibe se for "granted".
 *
 * @param notif    Notificação que originou o pop-up.
 * @param onClick  Callback executado ao clicar no pop-up (após focar a janela).
 */
export function showBrowserNotification(
  notif: AppNotification,
  onClick?: () => void,
): void {
  if (!isBrowserNotificationSupported()) return;
  if (Notification.permission !== "granted") return;

  try {
    const notification = new Notification(notif.titulo, {
      body: notif.mensagem,
      icon: NOTIFICATION_ICON,
      tag: notif.id, // evita pop-ups duplicados para a mesma notificação
    });

    notification.onclick = () => {
      window.focus();
      onClick?.();
      notification.close();
    };
  } catch (err) {
    console.warn(
      "[BrowserNotification] Falha ao exibir notificação nativa:",
      err,
    );
  }
}
